export const SWR_REQUEST_DEFAULTS = {
  revalidateIfStale: true,
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
};

const legacyHost =
  process.env.NEXT_PUBLIC_BACKEND_DOMAIN || 'lovevery-stage.com';

export const API_URLS = {
  ACCOUNT_MANAGER_PROXY: '/api/account-manager/api/v1',
  PLAYBACK_SERVICE_PROXY: '/api/playback-service',
  SUBSCRIPTION_MANAGER_PROXY: '/api/subscription-manager',
  PRODUCT_CATALOG_PROXY: '/api/product-catalog',
  ORDER_MANAGER_PROXY: '/api/order-manager',
  SUBSCRIPTION_DOWNGRADE: `/api/v1/subscription_downgrade`,
  PLAYBACK_SERVICE: `https://playback-service.${legacyHost}/api/v1/`,
  ACCOUNT_MANAGER: `https://account-manager.${legacyHost}/api/v1/`,
  PRODUCT_CATALOG: `https://product-catalog.${legacyHost}/api/v1/`,
  SUBSCRIPTION_MANAGER: `https://subscription-manager.${legacyHost}/api/v1/`,
  PORTFOLIO_MANAGER: `https://portfolio-manager.${legacyHost}/api/v1/`,
  CART_SERVICE: '/api/cart',
  ORCHESTRATION_SUBFLOW: 'https://lovevery.com/api/v1/orchestration_subflow',
};

/**
 * In some cases we will receive text/html back from the server, in which case we need to guard against an error
 * occurring when we attempt to parse the response as JSON
 *
 * @param response
 * @returns a promise that resolves with parsed json data or undefined if the response is not JSON
 */
export async function getJSONData(response: Response) {
  const isJson = response.headers
    .get('content-type')
    ?.includes('application/json');
  return isJson && (await response.json());
}

/**
 * Supports parsing errors from variously formatted response bodies
 *
 * @param response
 */
export async function parseErrorMessage(response: Response) {
  const json = await getJSONData(response);
  const statusMessage = `[${response.status}]: ${response.statusText}`;

  /**
   * our message is here:
   *
   * {
   *   error: {
   *     message: "oopsie daisy..."
   *   }
   * }
   *
   */
  if (typeof json?.error?.message === 'string') {
    return `${statusMessage} ${json?.error?.message}`;
  }

  /**
   * our message is here:
   *
   * {
   *   error: "oopsie daisy...",
   * }
   *
   */
  if (typeof json?.error === 'string') {
    return `${statusMessage} ${json?.error}`;
  }

  // Some errors are returned as a fallback message,
  // this returns the error to be rendered to the user
  if (typeof json?.error?.fallback === 'string') {
    return json.error.fallback;
  }

  /**
   * no additional message found in body, just return status & status text
   */
  return statusMessage;
}

/**
 * Shared Record Types
 *
 * NOTE:
 *   Some, but not ALL records will have these attributes.
 *   Before extending, ensure your data type actually has these attributes.
 *   If another pattern pops up feel free to add another shared interface here.
 */
export interface PGTimeStampedRecord {
  uuid: string;
  created_at: string;
  updated_at: string;
}

/**
 * API Response Types
 */
export type TypeResponseError = {
  ok: boolean;
  errorMessage: string;
};

export type TypeResponseSuccess<DataType> = {
  ok: boolean;
  data: DataType;
};

export const BASE_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};
